/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  combineReducers,
  composeReducers,
  composeSetters,
  entityStateMerger,
  errorsEntityState,
  fieldValidationSetter,
  newDirtyRow,
  onChangeReducer,
  removeDirtyRow,
  setDirty,
  setEntityField,
  setEntityFields,
  setNestedEntityField,
  setUpdatedDirtyType,
  Table,
  toOnChangeReducer,
} from '@gh/common-shared';
import { arrayIterator } from '@gh/core-util';
import {
  getSbItemFirstInvalidField,
  getSbListItemId,
  mergeSbItemTotal,
  SbListItem,
  validateSbListItem,
  validateSbListItemField,
} from '../../resources/sb-list.model';
import {
  querySbFirstInvalidItem,
  sbItemTable,
  SbListState,
  SbListStateEvent,
  SbListStateEventType,
} from './sb-list.state';

const setItemField = composeSetters(setEntityField,
                                    setUpdatedDirtyType,
                                    fieldValidationSetter(validateSbListItemField));
const complementSbListItem = entityStateMerger(errorsEntityState(validateSbListItem));

export const sbListReducer = onChangeReducer(
  composeReducers(
    sbLoadReducer,
    sbFilterReducer,
    sbSelectionReducer,
    combineReducers({
      items: sbItemsReducer,
    }),
  ),
  toOnChangeReducer(globalFlagsReducer),
);

function sbLoadReducer(state: SbListState, event: SbListStateEvent): SbListState {
  switch (event.type) {
    case SbListStateEventType.Load:
      return {
        ...state,
        originalItems: event.items,
        items: sbItemTable.create(arrayIterator(event.items)),
        formMode: event.mode,
        isDirty: false,
      };
    default:
      return state;
  }
}

function sbItemsReducer(items: Table<SbListItem>,
                        event: SbListStateEvent): Table<SbListItem> {
  switch (event.type) {
    case SbListStateEventType.ItemAddSet:
      return sbItemTable.mergeAll(items, event.items.map(complementSbListItem), newDirtyRow);
    case SbListStateEventType.ItemRemove:
      return sbItemTable.mergeById(items, event.itemId, removeDirtyRow);
    case SbListStateEventType.ItemSetField:
      return sbItemTable.mergeById(
        items,
        event.itemId,
        (item) => mergeSbItemTotal(setItemField(item, event.name, event.value)));
    default:
      return items;
  }
}

function sbFilterReducer(state: SbListState, event: SbListStateEvent): SbListState {
  switch (event.type) {
    case SbListStateEventType.FilterSetField:
      return setNestedEntityField(state, ['filter', event.name], event.value);
    default:
      return state;
  }
}

function sbSelectionReducer(state: SbListState, event: SbListStateEvent): SbListState {
  switch (event.type) {
    case SbListStateEventType.NavigateToInvalid: {
      const invalidItem = querySbFirstInvalidItem(state);
      if (invalidItem) {
        const invalidField = getSbItemFirstInvalidField(invalidItem);
        if (invalidField) {
          return setEntityFields(state, {
            filter: setEntityFields(
              state.filter,
              { type: invalidItem.budgetType, year: invalidItem.budgetYear }),
            selection: setEntityField(
              state.selection,
              'itemCell',
              { id: getSbListItemId(invalidItem), field: invalidField }),
          });
        }
      }
      return state;
    }
    case SbListStateEventType.ResetSelectedId:
      return setNestedEntityField(state, ['selection', 'itemId'], void 0);
    case SbListStateEventType.ResetFocusedCell:
      return setNestedEntityField(state, ['selection', 'itemCell'], void 0);
    default:
      return state;
  }
}

function globalFlagsReducer(state: SbListState, event: SbListStateEvent): SbListState {
  switch (event.type) {
    case SbListStateEventType.ItemAddSet:
    case SbListStateEventType.ItemRemove:
    case SbListStateEventType.ItemSetField:
      return setDirty(state);
    default:
      return state;
  }
}
